import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CenturionFooter from '@/components/CenturionFooter';
import {
  getMagazine,
  updateMagazine,
  getArticles,
  createArticle,
  updateArticle,
  deleteArticle,
  generateArticle,
  generateImage,
  uploadImage,
  downloadPDF,
  reorderArticles,
  getGalleryImages,
  addGalleryImage,
  deleteGalleryImage,
  getMagazineAnalytics,
  publishMagazineNow,
  unpublishMagazine,
  scheduleMagazine,
  notifyPublication,
  getSubscriberCount,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Eye,
  Share2,
  Download,
  Plus,
  Trash2,
  Sparkles,
  Image as ImageIcon,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Loader2,
  BookOpen,
  FileText,
  Wand2,
  Upload,
  Mail,
  MessageCircle,
  Copy,
  Check,
  X,
  Images,
  CalendarIcon,
  BarChart3,
  Globe,
  Clock,
  Users,
  TrendingUp,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Article Item Component
const SortableArticleItem = ({ article, isSelected, onSelect, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: article.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`article-item-${article.id}`}
      className={`group flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
        isSelected ? 'bg-[#FF6F61]/10' : 'hover:bg-[#F4F1EA]/50'
      }`}
      onClick={onSelect}
    >
      <div 
        {...attributes} 
        {...listeners}
        className="cursor-grab hover:text-[#FF6F61] touch-none"
      >
        <GripVertical className="w-4 h-4 text-[#4A5568]" />
      </div>
      <FileText className="w-4 h-4 text-[#4A5568]" />
      <span className="flex-1 text-sm text-[#4A5568] truncate" title={article.title || 'Untitled'}>
        {article.title || 'Untitled'}
      </span>
      <Button
        variant="ghost"
        size="icon"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 text-red-500"
      >
        <Trash2 className="w-3 h-3" />
      </Button>
    </div>
  );
};

const MagazineEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [magazine, setMagazine] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [sectionOpen, setSectionOpen] = useState({});
  
  // Dialogs
  const [isAIArticleOpen, setIsAIArticleOpen] = useState(false);
  const [isAIImageOpen, setIsAIImageOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isNewSectionOpen, setIsNewSectionOpen] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  
  // AI Generation
  const [aiTopic, setAiTopic] = useState('');
  const [aiContext, setAiContext] = useState('');
  const [aiLength, setAiLength] = useState('medium');
  const [aiImagePrompt, setAiImagePrompt] = useState('');
  const [generatingArticle, setGeneratingArticle] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  
  // New section
  const [newSectionName, setNewSectionName] = useState('');
  
  // Share
  const [copied, setCopied] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Gallery
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [newImageName, setNewImageName] = useState('');

  // Publishing & Analytics
  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [notifyOnPublish, setNotifyOnPublish] = useState(true);
  const [subscriberCount, setSubscriberCount] = useState(0);

  // Resizable sidebar
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const sidebarRef = React.useRef(null);
  const isResizingRef = React.useRef(false);

  const handleMouseDown = useCallback((e) => {
    isResizingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const startX = e.clientX;
    const startWidth = sidebarWidth;
    const onMouseMove = (e) => {
      if (!isResizingRef.current) return;
      const newWidth = Math.min(Math.max(startWidth + (e.clientX - startX), 220), 600);
      setSidebarWidth(newWidth);
    };
    const onMouseUp = () => {
      isResizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [sidebarWidth]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchData = useCallback(async () => {
    try {
      const [magRes, artRes, subCountRes] = await Promise.all([
        getMagazine(id),
        getArticles(id),
        getSubscriberCount(),
      ]);
      setMagazine(magRes.data);
      setArticles(artRes.data);
      setSubscriberCount(subCountRes.data.count);
      
      // Open all sections by default
      const openState = {};
      magRes.data.sections?.forEach(s => { openState[s.id] = true; });
      setSectionOpen(openState);
      
      if (magRes.data.sections?.length > 0) {
        setSelectedSection(magRes.data.sections[0].id);
      }
    } catch (error) {
      toast.error('Failed to load magazine');
      navigate('/admin/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMagazine(id, magazine);
      toast.success('Magazine saved');
    } catch (error) {
      toast.error('Failed to save magazine');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSection = async () => {
    if (!newSectionName.trim()) {
      toast.error('Please enter a section name');
      return;
    }
    
    const newSection = {
      id: crypto.randomUUID(),
      name: newSectionName,
      order: magazine.sections?.length || 0,
    };
    
    const updatedSections = [...(magazine.sections || []), newSection];
    const updatedMagazine = { ...magazine, sections: updatedSections };
    
    try {
      await updateMagazine(id, { sections: updatedSections });
      setMagazine(updatedMagazine);
      setSectionOpen({ ...sectionOpen, [newSection.id]: true });
      setSelectedSection(newSection.id);
      setNewSectionName('');
      setIsNewSectionOpen(false);
      toast.success('Section added');
    } catch (error) {
      toast.error('Failed to add section');
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section and all its articles?')) return;
    
    const updatedSections = magazine.sections.filter(s => s.id !== sectionId);
    
    try {
      await updateMagazine(id, { sections: updatedSections });
      // Delete articles in this section
      const sectionArticles = articles.filter(a => a.section_id === sectionId);
      await Promise.all(sectionArticles.map(a => deleteArticle(a.id)));
      
      setMagazine({ ...magazine, sections: updatedSections });
      setArticles(articles.filter(a => a.section_id !== sectionId));
      
      if (selectedSection === sectionId) {
        setSelectedSection(updatedSections[0]?.id || null);
      }
      toast.success('Section deleted');
    } catch (error) {
      toast.error('Failed to delete section');
    }
  };

  const handleAddArticle = async (sectionId) => {
    try {
      const sectionArticles = articles.filter(a => a.section_id === sectionId);
      const response = await createArticle({
        magazine_id: id,
        section_id: sectionId,
        title: 'New Article',
        content: '',
        order: sectionArticles.length,
      });
      setArticles([...articles, response.data]);
      setSelectedArticle(response.data);
      toast.success('Article created');
    } catch (error) {
      toast.error('Failed to create article');
    }
  };

  const handleUpdateArticle = async (articleId, updates) => {
    try {
      await updateArticle(articleId, updates);
      setArticles(articles.map(a => a.id === articleId ? { ...a, ...updates } : a));
      if (selectedArticle?.id === articleId) {
        setSelectedArticle({ ...selectedArticle, ...updates });
      }
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Delete this article?')) return;
    
    try {
      await deleteArticle(articleId);
      setArticles(articles.filter(a => a.id !== articleId));
      if (selectedArticle?.id === articleId) {
        setSelectedArticle(null);
      }
      toast.success('Article deleted');
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  // Handle drag end for article reordering
  const handleDragEnd = async (event, sectionId) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const sectionArticles = articles.filter(a => a.section_id === sectionId);
      const oldIndex = sectionArticles.findIndex(a => a.id === active.id);
      const newIndex = sectionArticles.findIndex(a => a.id === over.id);
      
      const reorderedSectionArticles = arrayMove(sectionArticles, oldIndex, newIndex);
      const otherArticles = articles.filter(a => a.section_id !== sectionId);
      const newArticles = [...otherArticles, ...reorderedSectionArticles.map((a, idx) => ({ ...a, order: idx }))];
      
      setArticles(newArticles);
      
      // Save to backend
      try {
        await reorderArticles(id, reorderedSectionArticles.map(a => a.id));
        toast.success('Articles reordered');
      } catch (error) {
        toast.error('Failed to save order');
        fetchData(); // Revert on error
      }
    }
  };

  // Gallery functions
  const fetchGalleryImages = async () => {
    setGalleryLoading(true);
    try {
      const response = await getGalleryImages();
      setGalleryImages(response.data);
    } catch (error) {
      toast.error('Failed to load gallery');
    } finally {
      setGalleryLoading(false);
    }
  };

  const handleAddToGallery = async (imageUrl, name) => {
    try {
      const response = await addGalleryImage({
        name: name || 'Untitled Image',
        image_url: imageUrl,
        tags: [],
      });
      setGalleryImages([response.data, ...galleryImages]);
      toast.success('Image added to gallery');
    } catch (error) {
      toast.error('Failed to add to gallery');
    }
  };

  const handleSelectFromGallery = async (imageUrl) => {
    if (selectedArticle) {
      await handleUpdateArticle(selectedArticle.id, { image_url: imageUrl });
      setIsGalleryOpen(false);
      toast.success('Image applied');
    }
  };

  const handleDeleteFromGallery = async (imageId) => {
    try {
      await deleteGalleryImage(imageId);
      setGalleryImages(galleryImages.filter(img => img.id !== imageId));
      toast.success('Image removed from gallery');
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleGalleryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const response = await uploadImage(file);
      if (response.data.success) {
        await handleAddToGallery(response.data.image_url, file.name);
      }
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  // Publishing functions
  const handlePublishNow = async () => {
    setPublishing(true);
    try {
      await publishMagazineNow(id);
      setMagazine({ ...magazine, published: true, scheduled_publish_date: null });
      toast.success('Magazine published!');
      
      // Send notification to subscribers if enabled
      if (notifyOnPublish && subscriberCount > 0) {
        try {
          const notifyRes = await notifyPublication(id);
          toast.success(`Notification sent to ${notifyRes.data.sent_count} subscribers`);
        } catch (e) {
          toast.error('Published but failed to send notifications');
        }
      }
      
      setIsPublishOpen(false);
    } catch (error) {
      toast.error('Failed to publish magazine');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    setPublishing(true);
    try {
      await unpublishMagazine(id);
      setMagazine({ ...magazine, published: false });
      toast.success('Magazine unpublished');
      setIsPublishOpen(false);
    } catch (error) {
      toast.error('Failed to unpublish magazine');
    } finally {
      setPublishing(false);
    }
  };

  const handleSchedulePublish = async () => {
    if (!scheduledDate) {
      toast.error('Please select a date');
      return;
    }
    setPublishing(true);
    try {
      const dateStr = scheduledDate.toISOString();
      await scheduleMagazine(id, dateStr);
      setMagazine({ ...magazine, scheduled_publish_date: dateStr });
      toast.success(`Scheduled for ${format(scheduledDate, 'PPP')}`);
      setIsPublishOpen(false);
    } catch (error) {
      toast.error('Failed to schedule publication');
    } finally {
      setPublishing(false);
    }
  };

  // Analytics functions
  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await getMagazineAnalytics(id);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleGenerateArticle = async () => {
    if (!aiTopic.trim()) {
      toast.error('Please enter a topic');
      return;
    }
    
    setGeneratingArticle(true);
    try {
      const response = await generateArticle({
        topic: aiTopic,
        context: aiContext,
        length: aiLength,
        style: 'warm, engaging, and suitable for a Bahamian community magazine',
      });
      
      if (response.data.success) {
        // Create a new article with the generated content
        const artResponse = await createArticle({
          magazine_id: id,
          section_id: selectedSection,
          title: response.data.title,
          content: response.data.content,
          order: articles.filter(a => a.section_id === selectedSection).length,
        });
        
        setArticles([...articles, artResponse.data]);
        setSelectedArticle(artResponse.data);
        setIsAIArticleOpen(false);
        setAiTopic('');
        setAiContext('');
        toast.success('Article generated!');
      }
    } catch (error) {
      toast.error('Failed to generate article');
    } finally {
      setGeneratingArticle(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!aiImagePrompt.trim()) {
      toast.error('Please describe the image you want');
      return;
    }
    
    setGeneratingImage(true);
    try {
      const response = await generateImage({
        prompt: aiImagePrompt,
        style: 'professional magazine photography with tropical Bahamian vibes',
      });
      
      if (response.data.success && selectedArticle) {
        const imageUrl = `data:${response.data.mime_type};base64,${response.data.image_data}`;
        await handleUpdateArticle(selectedArticle.id, { image_url: imageUrl });
        setIsAIImageOpen(false);
        setAiImagePrompt('');
        toast.success('Image generated!');
      }
    } catch (error) {
      toast.error('Failed to generate image');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedArticle) return;
    
    try {
      const response = await uploadImage(file);
      if (response.data.success) {
        await handleUpdateArticle(selectedArticle.id, { image_url: response.data.image_url });
        toast.success('Image uploaded');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const response = await uploadImage(file);
      if (response.data.success) {
        const updated = { ...magazine, cover_image: response.data.image_url };
        await updateMagazine(id, { cover_image: response.data.image_url });
        setMagazine(updated);
        toast.success('Cover image updated');
      }
    } catch (error) {
      toast.error('Failed to upload cover image');
    }
  };

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const response = await downloadPDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${magazine?.title || 'magazine'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const getShareUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/view/${id}`;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getShareUrl());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copied!');
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(`Check out: ${magazine?.title}`);
    const body = encodeURIComponent(`I'd like to share this magazine with you:\n\n${magazine?.title}\n${magazine?.description || ''}\n\nView it here: ${getShareUrl()}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(`Check out this magazine: ${magazine?.title}\n${getShareUrl()}`);
    window.open(`https://wa.me/?text=${text}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="spinner" style={{ width: '48px', height: '48px' }} />
      </div>
    );
  }

  const sectionArticles = selectedSection 
    ? articles.filter(a => a.section_id === selectedSection)
    : [];

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-to-dashboard-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#4A5568] hover:text-[#0A192F]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <Input
                data-testid="magazine-title-edit"
                value={magazine?.title || ''}
                onChange={(e) => setMagazine({ ...magazine, title: e.target.value })}
                className="font-serif text-xl border-none bg-transparent p-0 h-auto focus:ring-0 text-[#0A192F]"
                placeholder="Magazine Title"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              data-testid="analytics-btn"
              variant="outline"
              size="sm"
              onClick={() => { setIsAnalyticsOpen(true); fetchAnalytics(); }}
              className="border-[#E8E2D2] text-[#4A5568] hover:bg-[#F4F1EA]"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </Button>
            <Button
              data-testid="publish-btn"
              variant="outline"
              size="sm"
              onClick={() => setIsPublishOpen(true)}
              className={`border-[#E8E2D2] ${magazine?.published ? 'text-green-600 border-green-200 bg-green-50' : 'text-[#4A5568]'} hover:bg-[#F4F1EA]`}
            >
              <Globe className="w-4 h-4 mr-2" />
              {magazine?.published ? 'Published' : magazine?.scheduled_publish_date ? 'Scheduled' : 'Publish'}
            </Button>
            <Button
              data-testid="preview-magazine-btn"
              variant="outline"
              size="sm"
              onClick={() => navigate(`/view/${id}`)}
              className="border-[#E8E2D2] text-[#4A5568] hover:bg-[#F4F1EA]"
            >
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button
              data-testid="share-magazine-btn"
              variant="outline"
              size="sm"
              onClick={() => setIsShareOpen(true)}
              className="border-[#E8E2D2] text-[#4A5568] hover:bg-[#F4F1EA]"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
            <Button
              data-testid="save-magazine-btn"
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="btn-coral"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Sections & Articles (Resizable) */}
        <aside
          ref={sidebarRef}
          className="border-r border-[#E8E2D2] bg-white flex flex-col relative flex-shrink-0"
          style={{ width: `${sidebarWidth}px` }}
        >
          <div className="p-4 border-b border-[#E8E2D2]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-[#0A192F]">Sections</h3>
              <Button
                data-testid="add-section-btn"
                variant="ghost"
                size="icon"
                onClick={() => setIsNewSectionOpen(true)}
                className="h-8 w-8 text-[#FF6F61] hover:bg-[#FF6F61]/10"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <ScrollArea className="flex-1">
            <div className="p-2">
              {magazine?.sections?.map((section) => (
                <Collapsible
                  key={section.id}
                  open={sectionOpen[section.id]}
                  onOpenChange={(open) => setSectionOpen({ ...sectionOpen, [section.id]: open })}
                >
                  <div 
                    className={`group flex items-center gap-1 p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedSection === section.id ? 'bg-[#F4F1EA]' : 'hover:bg-[#F4F1EA]/50'
                    }`}
                    onClick={() => setSelectedSection(section.id)}
                  >
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6 p-0">
                        {sectionOpen[section.id] ? (
                          <ChevronDown className="w-4 h-4 text-[#4A5568]" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-[#4A5568]" />
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <span className="flex-1 text-sm font-medium text-[#0A192F] truncate">
                      {section.name}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); handleAddArticle(section.id); }}
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-[#FF6F61]"
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); handleDeleteSection(section.id); }}
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-red-500"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  
                  <CollapsibleContent>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event) => handleDragEnd(event, section.id)}
                    >
                      <SortableContext
                        items={articles.filter(a => a.section_id === section.id).map(a => a.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="ml-6 space-y-1 py-1">
                          {articles
                            .filter(a => a.section_id === section.id)
                            .sort((a, b) => a.order - b.order)
                            .map((article) => (
                              <SortableArticleItem
                                key={article.id}
                                article={article}
                                isSelected={selectedArticle?.id === article.id}
                                onSelect={() => setSelectedArticle(article)}
                                onDelete={() => handleDeleteArticle(article.id)}
                              />
                            ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          </ScrollArea>

          {/* Cover Image Section */}
          <div className="p-4 border-t border-[#E8E2D2]">
            <h4 className="text-sm font-medium text-[#0A192F] mb-2">Cover Image</h4>
            <div className="relative aspect-[4/3] bg-[#F4F1EA] rounded-lg overflow-hidden group">
              {magazine?.cover_image ? (
                <img
                  src={magazine.cover_image}
                  alt="Cover"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <BookOpen className="w-8 h-8 text-[#E8E2D2]" />
                </div>
              )}
              <label className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverImageUpload}
                  className="hidden"
                />
                <Upload className="w-6 h-6 text-white" />
              </label>
            </div>
          </div>
          {/* Drag handle for resizing */}
          <div
            data-testid="sidebar-resize-handle"
            onMouseDown={handleMouseDown}
            className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-[#40E0D0]/40 transition-colors z-20 group"
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-[#E8E2D2] group-hover:bg-[#40E0D0] transition-colors" />
          </div>
        </aside>

        {/* Main Content - Article Editor */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {selectedArticle ? (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Article Toolbar */}
              <div className="p-4 border-b border-[#E8E2D2] bg-white flex items-center gap-2 flex-wrap">
                <Button
                  data-testid="generate-ai-article-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAIArticleOpen(true)}
                  className="border-[#FF6F61] text-[#FF6F61] hover:bg-[#FF6F61]/10"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  AI Write
                </Button>
                <Button
                  data-testid="generate-ai-image-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAIImageOpen(true)}
                  className="border-[#FF6F61] text-[#FF6F61] hover:bg-[#FF6F61]/10"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  AI Image
                </Button>
                <Button
                  data-testid="open-gallery-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => { setIsGalleryOpen(true); fetchGalleryImages(); }}
                  className="border-[#E8E2D2] text-[#4A5568] hover:bg-[#F4F1EA]"
                >
                  <Images className="w-4 h-4 mr-2" />
                  Gallery
                </Button>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    data-testid="upload-image-btn"
                    variant="outline"
                    size="sm"
                    className="border-[#E8E2D2] text-[#4A5568]"
                    asChild
                  >
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Image
                    </span>
                  </Button>
                </label>
              </div>

              {/* Article Form */}
              <ScrollArea className="flex-1 p-6">
                <div className="max-w-3xl mx-auto space-y-6">
                  <Input
                    data-testid="article-title-input"
                    value={selectedArticle.title}
                    onChange={(e) => handleUpdateArticle(selectedArticle.id, { title: e.target.value })}
                    placeholder="Article Title"
                    className="font-serif text-3xl border-none bg-transparent p-0 h-auto focus:ring-0 text-[#0A192F]"
                  />
                  
                  {selectedArticle.image_url && (
                    <div className="relative group">
                      <img
                        src={selectedArticle.image_url}
                        alt="Article"
                        className="w-full rounded-xl object-cover max-h-[400px]"
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => handleUpdateArticle(selectedArticle.id, { image_url: null })}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  
                  <Textarea
                    data-testid="article-content-input"
                    value={selectedArticle.content}
                    onChange={(e) => handleUpdateArticle(selectedArticle.id, { content: e.target.value })}
                    placeholder="Write your article content here..."
                    className="min-h-[400px] text-lg leading-relaxed border-none bg-transparent p-0 focus:ring-0 text-[#4A5568] resize-none"
                  />
                </div>
              </ScrollArea>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div className="max-w-md">
                <FileText className="w-16 h-16 text-[#E8E2D2] mx-auto mb-4" />
                <h3 className="font-serif text-2xl text-[#0A192F] mb-2">
                  Select or Create an Article
                </h3>
                <p className="text-[#4A5568] mb-6">
                  Choose an article from the sidebar or create a new one to start editing.
                </p>
                {selectedSection && (
                  <Button
                    data-testid="create-first-article-btn"
                    onClick={() => handleAddArticle(selectedSection)}
                    className="btn-coral"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Article
                  </Button>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* New Section Dialog */}
      <Dialog open={isNewSectionOpen} onOpenChange={setIsNewSectionOpen}>
        <DialogContent className="bg-white border-[#E8E2D2] rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A192F]">Add New Section</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              data-testid="new-section-name-input"
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              placeholder="Section name (e.g., Lifestyle, Events)"
              className="input-bahamian"
            />
            <Button
              data-testid="add-section-submit-btn"
              onClick={handleAddSection}
              className="btn-coral w-full"
            >
              Add Section
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Article Dialog */}
      <Dialog open={isAIArticleOpen} onOpenChange={setIsAIArticleOpen}>
        <DialogContent className="bg-white border-[#E8E2D2] rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A192F] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#FF6F61]" />
              Generate Article with AI
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#4A5568] block mb-2">Topic</label>
              <Input
                data-testid="ai-topic-input"
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
                placeholder="e.g., Best beaches in Nassau"
                className="input-bahamian"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#4A5568] block mb-2">
                Additional Context (optional)
              </label>
              <Textarea
                data-testid="ai-context-input"
                value={aiContext}
                onChange={(e) => setAiContext(e.target.value)}
                placeholder="Any specific details, facts, or angle you want covered..."
                className="input-bahamian min-h-[100px]"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#4A5568] block mb-2">Length</label>
              <Select value={aiLength} onValueChange={setAiLength}>
                <SelectTrigger data-testid="ai-length-select" className="input-bahamian">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Short (~200 words)</SelectItem>
                  <SelectItem value="medium">Medium (~350 words)</SelectItem>
                  <SelectItem value="long">Long (~600 words)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="generate-article-submit-btn"
              onClick={handleGenerateArticle}
              disabled={generatingArticle}
              className="btn-coral w-full"
            >
              {generatingArticle ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </span>
              ) : (
                'Generate Article'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Image Dialog */}
      <Dialog open={isAIImageOpen} onOpenChange={setIsAIImageOpen}>
        <DialogContent className="bg-white border-[#E8E2D2] rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A192F] flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-[#FF6F61]" />
              Generate Image with AI
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#4A5568] block mb-2">
                Describe the image you want
              </label>
              <Textarea
                data-testid="ai-image-prompt-input"
                value={aiImagePrompt}
                onChange={(e) => setAiImagePrompt(e.target.value)}
                placeholder="e.g., A beautiful sunset over the ocean with palm trees silhouetted against the sky"
                className="input-bahamian min-h-[120px]"
              />
            </div>
            <Button
              data-testid="generate-image-submit-btn"
              onClick={handleGenerateImage}
              disabled={generatingImage}
              className="btn-coral w-full"
            >
              {generatingImage ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </span>
              ) : (
                'Generate Image'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={isShareOpen} onOpenChange={setIsShareOpen}>
        <DialogContent className="bg-white border-[#E8E2D2] rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A192F] flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#FF6F61]" />
              Share Magazine
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Share Link */}
            <div>
              <label className="text-sm font-medium text-[#4A5568] block mb-2">
                Share Link
              </label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={getShareUrl()}
                  className="input-bahamian flex-1"
                />
                <Button
                  data-testid="copy-link-btn"
                  onClick={handleCopyLink}
                  variant="outline"
                  className="border-[#E8E2D2]"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Share Options */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                data-testid="share-email-btn"
                onClick={handleShareEmail}
                variant="outline"
                className="border-[#E8E2D2] text-[#4A5568] hover:bg-[#F4F1EA]"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email
              </Button>
              <Button
                data-testid="share-whatsapp-btn"
                onClick={handleShareWhatsApp}
                variant="outline"
                className="border-[#E8E2D2] text-green-600 hover:bg-green-50"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>

            {/* Download PDF */}
            <Button
              data-testid="download-pdf-btn"
              onClick={handleDownloadPDF}
              disabled={downloadingPDF}
              className="btn-coral w-full"
            >
              {downloadingPDF ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating PDF...
                </span>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Gallery Dialog */}
      <Dialog open={isGalleryOpen} onOpenChange={setIsGalleryOpen}>
        <DialogContent className="bg-white border-[#E8E2D2] rounded-3xl max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A192F] flex items-center gap-2">
              <Images className="w-5 h-5 text-[#FF6F61]" />
              Image Gallery
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="gallery" className="mt-4">
            <TabsList className="grid w-full grid-cols-2 bg-[#F4F1EA]">
              <TabsTrigger value="gallery" className="data-[state=active]:bg-white">
                Browse Gallery
              </TabsTrigger>
              <TabsTrigger value="upload" className="data-[state=active]:bg-white">
                Upload New
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="gallery" className="mt-4">
              {galleryLoading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner" style={{ width: '32px', height: '32px' }} />
                </div>
              ) : galleryImages.length === 0 ? (
                <div className="text-center py-12">
                  <Images className="w-12 h-12 text-[#E8E2D2] mx-auto mb-3" />
                  <p className="text-[#4A5568]">No images in gallery yet</p>
                  <p className="text-sm text-[#4A5568]/70 mt-1">Upload images to reuse them across articles</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="grid grid-cols-3 gap-3 p-1">
                    {galleryImages.map((img) => (
                      <div
                        key={img.id}
                        className="relative group aspect-square rounded-xl overflow-hidden border border-[#E8E2D2] cursor-pointer hover:border-[#FF6F61] transition-colors"
                        onClick={() => handleSelectFromGallery(img.image_url)}
                      >
                        <img
                          src={img.image_url}
                          alt={img.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white text-sm font-medium">Select</span>
                        </div>
                        <Button
                          variant="destructive"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleDeleteFromGallery(img.id); }}
                          className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                          <p className="text-white text-xs truncate">{img.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>
            
            <TabsContent value="upload" className="mt-4">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#4A5568] block mb-2">
                    Image Name (optional)
                  </label>
                  <Input
                    data-testid="gallery-image-name-input"
                    value={newImageName}
                    onChange={(e) => setNewImageName(e.target.value)}
                    placeholder="e.g., Beach sunset photo"
                    className="input-bahamian"
                  />
                </div>
                <div className="border-2 border-dashed border-[#E8E2D2] rounded-xl p-8 text-center">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleGalleryUpload}
                    className="hidden"
                    id="gallery-upload"
                  />
                  <label htmlFor="gallery-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 text-[#4A5568] mx-auto mb-3" />
                    <p className="text-[#0A192F] font-medium">Click to upload</p>
                    <p className="text-sm text-[#4A5568] mt-1">PNG, JPG up to 10MB</p>
                  </label>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Publish Dialog */}
      <Dialog open={isPublishOpen} onOpenChange={setIsPublishOpen}>
        <DialogContent className="bg-white border-[#E8E2D2] rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A192F] flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#FF6F61]" />
              Publish Magazine
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Current Status */}
            <div className="p-4 rounded-xl bg-[#F4F1EA]">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${magazine?.published ? 'bg-green-500' : magazine?.scheduled_publish_date ? 'bg-yellow-500' : 'bg-gray-400'}`} />
                <span className="text-sm font-medium text-[#0A192F]">
                  {magazine?.published ? 'Currently Published' : magazine?.scheduled_publish_date ? `Scheduled: ${format(new Date(magazine.scheduled_publish_date), 'PPP')}` : 'Not Published'}
                </span>
              </div>
            </div>

            {/* Publish Now */}
            {!magazine?.published && (
              <>
                {subscriberCount > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-[#F4F1EA] rounded-xl">
                    <input
                      type="checkbox"
                      id="notify-subscribers"
                      checked={notifyOnPublish}
                      onChange={(e) => setNotifyOnPublish(e.target.checked)}
                      className="w-4 h-4 rounded border-[#E8E2D2] text-[#FF6F61] focus:ring-[#FF6F61]"
                    />
                    <label htmlFor="notify-subscribers" className="text-sm text-[#4A5568]">
                      Notify {subscriberCount} subscriber{subscriberCount !== 1 ? 's' : ''} by email
                    </label>
                  </div>
                )}
                <Button
                  data-testid="publish-now-btn"
                  onClick={handlePublishNow}
                  disabled={publishing}
                  className="btn-coral w-full"
                >
                  {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Globe className="w-4 h-4 mr-2" />}
                  Publish Now
                </Button>
              </>
            )}

            {/* Unpublish */}
            {magazine?.published && (
              <Button
                data-testid="unpublish-btn"
                onClick={handleUnpublish}
                disabled={publishing}
                variant="outline"
                className="w-full border-red-200 text-red-600 hover:bg-red-50"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Unpublish
              </Button>
            )}

            {/* Schedule Publishing */}
            {!magazine?.published && (
              <div className="border-t border-[#E8E2D2] pt-4">
                <label className="text-sm font-medium text-[#4A5568] block mb-3">
                  Or Schedule for Later
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal border-[#E8E2D2]"
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {scheduledDate ? format(scheduledDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white" align="start">
                    <Calendar
                      mode="single"
                      selected={scheduledDate}
                      onSelect={setScheduledDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {scheduledDate && (
                  <Button
                    data-testid="schedule-publish-btn"
                    onClick={handleSchedulePublish}
                    disabled={publishing}
                    variant="outline"
                    className="w-full mt-3 border-[#FF6F61] text-[#FF6F61] hover:bg-[#FF6F61]/10"
                  >
                    {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Clock className="w-4 h-4 mr-2" />}
                    Schedule Publication
                  </Button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={isAnalyticsOpen} onOpenChange={setIsAnalyticsOpen}>
        <DialogContent className="bg-white border-[#E8E2D2] rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#0A192F] flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#FF6F61]" />
              Magazine Analytics
            </DialogTitle>
          </DialogHeader>
          
          {analyticsLoading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" style={{ width: '32px', height: '32px' }} />
            </div>
          ) : analytics ? (
            <div className="space-y-6 mt-4">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-gradient-to-br from-[#40E0D0]/10 to-[#00CED1]/10 border border-[#40E0D0]/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-4 h-4 text-[#00897B]" />
                    <span className="text-xs text-[#4A5568] uppercase tracking-wider">Total Views</span>
                  </div>
                  <p className="text-3xl font-bold text-[#0A192F]">{analytics.total_views}</p>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-[#FF6F61]/10 to-[#FF7F50]/10 border border-[#FF6F61]/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-[#FF6F61]" />
                    <span className="text-xs text-[#4A5568] uppercase tracking-wider">Unique Visitors</span>
                  </div>
                  <p className="text-3xl font-bold text-[#0A192F]">{analytics.unique_views}</p>
                </div>
              </div>

              {/* Views Over Time */}
              {analytics.views_by_date?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[#4A5568] mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Views Over Time (Last 30 Days)
                  </h4>
                  <div className="space-y-2">
                    {analytics.views_by_date.slice(-7).map((day) => (
                      <div key={day.date} className="flex items-center gap-3">
                        <span className="text-xs text-[#4A5568] w-20">{format(new Date(day.date), 'MMM d')}</span>
                        <div className="flex-1 h-6 bg-[#F4F1EA] rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#40E0D0] to-[#00CED1] rounded-full transition-all"
                            style={{ width: `${Math.min((day.count / Math.max(...analytics.views_by_date.map(d => d.count))) * 100, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-[#0A192F] w-8 text-right">{day.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Views */}
              {analytics.recent_views?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[#4A5568] mb-3">Recent Views</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {analytics.recent_views.map((view, i) => (
                      <div key={i} className="flex items-center justify-between text-sm p-2 rounded-lg bg-[#F4F1EA]">
                        <span className="text-[#4A5568] truncate max-w-[200px]">
                          {view.viewer_ip || 'Anonymous'}
                        </span>
                        <span className="text-xs text-[#4A5568]">
                          {format(new Date(view.viewed_at), 'MMM d, h:mm a')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {analytics.total_views === 0 && (
                <div className="text-center py-8">
                  <BarChart3 className="w-12 h-12 text-[#E8E2D2] mx-auto mb-3" />
                  <p className="text-[#4A5568]">No views yet</p>
                  <p className="text-sm text-[#4A5568]/70 mt-1">Share your magazine to start tracking views</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-[#4A5568]">Failed to load analytics</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <CenturionFooter />
    </div>
  );
};

export default MagazineEditor;
